import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { SESSION_KEY } from "@/api/client";
import { useLogout, useSession, useTotpConfirm, useTotpSetup } from "@/api/hooks";
import { OtpInput } from "@/components/OtpInput";
import { Button, Checkbox, LoadError, Notice, PageSpinner, Spinner } from "@/components/ui";
import { AuthLayout } from "@/layouts/AuthLayout";
import { errorMessage } from "@/lib/errors";
import { useCopy } from "@/lib/useCopy";
import { homeFor } from "@/router";

export function SetupPage() {
  const session = useSession();
  const [codes, setCodes] = useState<string[] | null>(null);

  // Once confirmed the session is active, but we stay here to show the recovery codes.
  if (codes) return <RecoveryCodes codes={codes} />;
  if (session.isPending) return <PageSpinner />;
  if (session.isError) return <LoadError onRetry={() => void session.refetch()} />;
  if (session.data.stage !== "pending_setup") return <Navigate to={homeFor(session.data.stage)} replace />;
  return <ScanStep onConfirmed={setCodes} />;
}

function ScanStep({ onConfirmed }: { onConfirmed: (codes: string[]) => void }) {
  const { t } = useTranslation();
  const setup = useTotpSetup();
  const confirm = useTotpConfirm();
  const logout = useLogout();
  const { copied, copy } = useCopy();
  const [code, setCode] = useState("");
  const started = useRef(false);

  // Each call makes a new secret, so request it once (StrictMode mounts effects twice).
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    setup.mutate();
  }, [setup]);

  const send = (value: string) => {
    if (confirm.isPending) return;
    confirm.mutate(value, { onSuccess: (res) => onConfirmed(res.recovery_codes) });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    send(code);
  };

  return (
    <AuthLayout step={2}>
      <div>
        <h1 className="m-0 mb-1.5 text-[21px] font-semibold">{t("auth.setupTitle")}</h1>
        <p className="m-0 text-muted">{t("auth.setupHint")}</p>
      </div>

      {setup.isError ? (
        <LoadError onRetry={() => setup.mutate()} />
      ) : !setup.data ? (
        <div className="flex items-center gap-2 text-muted" role="status">
          <Spinner /> {t("common.loading")}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-[18px]">
          <div
            role="img"
            aria-label={t("auth.qrLabel")}
            className="size-[148px] shrink-0 rounded-[var(--radius-ctl)] border border-border bg-white p-2 [&>svg]:block [&>svg]:size-full"
            // Server-generated SVG from our own API.
            dangerouslySetInnerHTML={{ __html: setup.data.qr_svg }}
          />
          <div className="flex min-w-[180px] flex-1 flex-col gap-2">
            <span className="label-caps">{t("auth.cantScan")}</span>
            {/* Groups of four that never split across lines, so the key is easy to type. */}
            <code
              data-testid="totp-secret"
              className="flex flex-wrap gap-x-[0.6em] rounded-md bg-surface-2 px-2 py-1.5 font-mono text-sm tracking-wider"
            >
              {setup.data.secret.match(/.{1,4}/g)?.map((group, i) => (
                <span key={i} className="whitespace-nowrap">
                  {group}
                </span>
              ))}
            </code>
            <Button size="sm" icon={copied ? "check" : "copy"} className="self-start" onClick={() => copy(setup.data.secret)}>
              {copied ? t("common.copied") : t("auth.copyKey")}
            </Button>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {confirm.isError && <Notice tone="blocked">{errorMessage(t, confirm.error)}</Notice>}
        <div className="flex flex-col gap-1.5">
          <span className="label-caps">{t("auth.enterCode")}</span>
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={send}
            invalid={confirm.isError}
            disabled={!setup.data || confirm.isPending}
          />
        </div>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          icon="lock"
          loading={confirm.isPending}
          disabled={!setup.data || code.length !== 6}
        >
          {t("auth.verifyAndContinue")}
        </Button>
        <Button variant="ghost" size="sm" className="self-start" onClick={() => logout.mutate()}>
          {t("auth.otherAccount")}
        </Button>
      </form>
    </AuthLayout>
  );
}

function RecoveryCodes({ codes }: { codes: string[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const session = useSession();
  const { copied, copy } = useCopy();
  const [saved, setSaved] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const text = codes.join("\n");

  const download = () => {
    const username = session.data?.user?.username ?? "";
    const body = `${t("auth.recoveryFileHeader", { username })}\n\n${text}\n`;
    const url = URL.createObjectURL(new Blob([body], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "mixla-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const finish = async () => {
    setLeaving(true);
    await qc.refetchQueries({ queryKey: SESSION_KEY });
    navigate("/projects", { replace: true });
  };

  return (
    <AuthLayout step={3}>
      <div>
        <h1 className="m-0 mb-1.5 text-[21px] font-semibold">{t("auth.recoveryTitle")}</h1>
        <p className="m-0 text-muted">{t("auth.recoveryIntro")}</p>
      </div>
      <ul className="m-0 grid list-none grid-cols-2 gap-2 rounded-[var(--radius-card)] bg-surface-2 p-4 font-mono text-[15px] tracking-wide">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" icon={copied ? "check" : "copy"} onClick={() => copy(text)}>
          {copied ? t("common.copied") : t("auth.copyAll")}
        </Button>
        <Button size="sm" icon="download" onClick={download}>
          {t("auth.downloadTxt")}
        </Button>
      </div>
      <Checkbox label={t("auth.savedCheckbox")} checked={saved} onChange={(e) => setSaved(e.target.checked)} />
      <Button variant="primary" size="lg" disabled={!saved} loading={leaving} onClick={() => void finish()}>
        {t("auth.finish")}
      </Button>
    </AuthLayout>
  );
}
